<template>
    <div class="base-table">
        <div class="base-table__desktop">
            <el-table
                ref="table"
                :data="items"
                :empty-text="emptyText"
                highlight-current-row
                style="width: 100%"
                class="base-table__content"
                @selection-change="onSelectionChange"
            >
            <el-table-column
                v-if="checkbox"
                type="selection"
                width="50px"
            />
            <el-table-column
                v-for="header in headers"
                :key="header.key || header.label"
                :prop="header.key"
                :label="header.label"
                :min-width="header.minWidth || '140px'"
                :width="header.width"
                :align="header.align || 'left'"
                :label-class-name="labelClassName"
            >
                <template v-slot:default="scope">
                    <slot
                        v-if="header.type === 'slot'"
                        :name="header.key"
                        :row="scope.row"
                    />
                    <el-tooltip
                        v-else-if="header.type === 'html' && header.tooltip"
                        effect="dark"
                        :content="getTooltipContent(scope.row, header)"
                        placement="top"
                    >
                        <div v-html="getCellValue(scope.row, header)" />
                    </el-tooltip>
                    <div v-else-if="header.type === 'html'" v-html="getCellValue(scope.row, header)" />
                    <div v-else-if="header.type === 'actions'" class="base-table__actions">
                        <el-dropdown
                            v-if="actionButtonType === 'dropdown'"
                            class="base-table__action-button"
                            trigger="click"
                        >
                            <span class="el-dropdown-link">
                                <i class="mdi mdi-dots-vertical" />
                            </span>
                            <el-dropdown-menu v-slot="dropdown">
                                <el-dropdown-item
                                    v-for="action in header.actions || actions"
                                    :key="action.emit"
                                    :icon="action.icon"
                                    :disabled="isActionDisabled(action, scope.row)"
                                    @click.native="emitAction(action.emit, scope.row)"
                                >
                                    {{ action.label }}
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                        <div v-else class="base-table__inline-actions">
                            <el-button
                                v-for="action in header.actions || actions"
                                :key="action.emit"
                                :type="action.buttonType || 'text'"
                                :icon="action.icon"
                                :disabled="isActionDisabled(action, scope.row)"
                                size="mini"
                                @click="emitAction(action.emit, scope.row)"
                            >
                                {{ action.label }}
                            </el-button>
                        </div>
                    </div>
                    <div v-else class="cell-text">{{ getCellValue(scope.row, header) || '-' }}</div>
                </template>
            </el-table-column>
        </el-table>
    </div>

    <div class="base-table__mobile">
        <div class="base-table__cards">
            <div v-if="items.length === 0" class="base-table__empty">
                {{ emptyText }}
            </div>
            <div
                v-for="(item, index) in items"
                :key="index"
                class="base-table__card"
            >
                <div class="base-table__card-header">
                    <el-checkbox
                        v-if="checkbox"
                        v-model="cardSelection[index]"
                        @change="onCardSelectionChange(index)"
                    />
                    <div class="base-table__card-title">
                        {{ getCardTitle(item) }}
                    </div>
                    <div v-if="hasActions" class="base-table__card-actions">
                        <el-dropdown trigger="click" size="small">
                            <span class="el-dropdown-link">
                                <i class="mdi mdi-dots-vertical" />
                            </span>
                            <el-dropdown-menu v-slot="dropdown">
                                <el-dropdown-item
                                    v-for="action in actions"
                                    :key="action.emit"
                                    :icon="action.icon"
                                    :disabled="isActionDisabled(action, item)"
                                    @click.native="emitAction(action.emit, item)"
                                >
                                    {{ action.label }}
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </div>
                </div>
                <div class="base-table__card-body">
                    <div
                        v-for="header in dataHeaders"
                        :key="header.key || header.label"
                        class="base-table__card-row"
                    >
                        <div class="base-table__card-label">{{ header.label }}</div>
                        <div class="base-table__card-value">
                            <slot
                                v-if="header.type === 'slot'"
                                :name="header.key"
                                :row="item"
                            />
                            <span v-else-if="header.type === 'html'" v-html="getCellValue(item, header)" />
                            <span v-else>{{ getCellValue(item, header) || '-' }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="showPagination" class="base-table__pagination">
            <div class="total">
                <span>{{ totalItems }} {{ resultsLabel }}</span>
                <div style="margin: 0 20px"></div>
                <span>{{ viewsByPageLabel }}:</span>
                <el-pagination
                    :key="`sizes-${$i18n.locale}`"
                    @size-change="handleSizeChange"
                    :current-page="currentPage"
                    :page-sizes="pageSizes"
                    :page-size="pageSize"
                    layout="sizes"
                    :total="totalItems"
                />
            </div>
            <div class="prev_next">
                <el-pagination
                    :key="`pager-${$i18n.locale}`"
                    @current-change="handleCurrentChange"
                    :current-page.sync="currentPage"
                    :page-sizes="pageSizes"
                    :page-size="pageSize"
                    layout="prev, pager, next, jumper"
                    :total="totalItems"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BaseTable',
    props: {
        checkbox: {
            type: Boolean,
            default: false
        },
        headers: {
            type: Array,
            required: true,
            validator(value) {
                return value.every(h => h.label && h.key);
            }
        },
        items: {
            type: Array,
            required: true
        },
        totalItems: {
            type: Number,
            default: 0
        },
        page: {
            type: Number,
            default: 1
        },
        pageSize: {
            type: Number,
            default: 10
        },
        pageSizes: {
            type: Array,
            default: () => [10, 20, 30, 40]
        },
        showPagination: {
            type: Boolean,
            default: false
        },
        resultsLabel: {
            type: String,
            default: 'resultados'
        },
        viewsByPageLabel: {
            type: String,
            default: 'Ver por página'
        },
        emptyText: {
            type: String,
            default: 'Sem dados'
        },
        actions: {
            type: Array,
            default: () => []
        },
        actionButtonType: {
            type: String,
            default: 'dropdown',
            validator(value) {
                return ['dropdown', 'inline'].includes(value);
            }
        },
        labelClassName: {
            type: String,
            default: 'base-table__label'
        },
        responsiveCards: {
            type: Boolean,
            default: true
        },
        cardTitle: {
            type: [String, Function],
            default: null
        }
    },
    data() {
        return {
            currentPage: this.page,
            currentPageSize: this.pageSize,
            selectedRows: [],
            cardSelection: []
        };
    },
    computed: {
        dataHeaders() {
            return this.headers.filter(h => h.type !== 'actions');
        },
        hasActions() {
            return this.actions.length > 0;
        }
    },
    watch: {
        page(value) {
            this.currentPage = value;
        },
        pageSize(value) {
            this.currentPageSize = value;
        },
        items: {
            handler(newItems) {
                this.cardSelection = new Array(newItems.length).fill(false);
            },
            immediate: true
        }
    },
    methods: {
        onSelectionChange(rows) {
            this.selectedRows = rows;
            this.$emit('selectionChange', rows);
        },
        onCardSelectionChange(index) {
            const selected = this.items.filter((_, i) => this.cardSelection[i]);
            this.selectedRows = selected;
            this.$emit('selectionChange', selected);
        },
        getCardTitle(item) {
            if (!this.cardTitle) {
                const firstHeader = this.dataHeaders[0];
                return firstHeader ? this.getCellValue(item, firstHeader) : '';
            }
            if (typeof this.cardTitle === 'function') {
                return this.cardTitle(item);
            }
            return item[this.cardTitle] || '-';
        },
        handleSizeChange(size) {
            this.currentPageSize = size;
            this.currentPage = 1;
            this.emitPagination();
        },
        handleCurrentChange(page) {
            this.currentPage = page;
            this.emitPagination();
        },
        emitPagination() {
            this.$emit('paginationChange', {
                page: this.currentPage,
                pageSize: this.currentPageSize
            });
        },
        getCellValue(row, header) {
            if (header.formatter && typeof header.formatter === 'function') {
                return header.formatter(row, header);
            }
            if (header.key.includes('.')) {
                return header.key.split('.').reduce((obj, key) => obj && obj[key], row);
            }
            return row[header.key];
        },
        getTooltipContent(row, header) {
            if (header.tooltipContent && typeof header.tooltipContent === 'function') {
                return header.tooltipContent(row, header);
            }
            const value = this.getCellValue(row, header);
            return typeof value === 'string' ? value.replace(/<[^>]*>/g, '') : value;
        },
        emitAction(eventName, row) {
            this.$emit(eventName, row);
            this.$emit('action', { eventName, row });
        },
        isActionDisabled(action, row) {
            if (action.disabled && typeof action.disabled === 'function') {
                return action.disabled(row);
            }
            return !!action.disabled;
        },
        clearSelection() {
            this.$refs.table.clearSelection();
            this.cardSelection = this.cardSelection.map(() => false);
            this.selectedRows = [];
            this.$emit('selectionChange', []);
        },
        toggleAllSelection() {
            this.$refs.table.toggleAllSelection();
            const allSelected = this.cardSelection.every(Boolean);
            this.cardSelection = this.cardSelection.map(() => !allSelected);
            this.onCardSelectionChange();
        }
    }
};
</script>

<style scoped lang="scss">
.base-table {
    margin-top: -8px;
    width: 100%;

    &__desktop {
        display: block;

        @media (max-width: 768px) {
            display: none;
        }
    }

    &__mobile {
        display: none;

        @media (max-width: 768px) {
            display: block;
        }
    }

    &__content {
        width: 100%;
    }

    &__cards {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 8px;
    }

    &__empty {
        padding: 40px 16px;
        text-align: center;
        color: #909399;
        font-size: 14px;
        background: #f5f7fa;
        border-radius: 8px;
    }

    &__card {
        background: #ffffff;
        border: 1px solid #ebeef5;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        padding: 16px;

        &-header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding-bottom: 12px;
            margin-bottom: 12px;
            border-bottom: 1px solid #ebeef5;
        }

        &-title {
            flex: 1;
            font-size: 15px;
            font-weight: 600;
            color: #183b57;
            word-break: break-word;
        }

        &-actions {
            flex-shrink: 0;
        }

        &-body {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        &-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
        }

        &-label {
            flex-shrink: 0;
            font-size: 13px;
            color: #7c7c7c;
            max-width: 45%;
        }

        &-value {
            flex: 1;
            text-align: right;
            font-size: 13px;
            color: #3b3b3b;
            word-break: break-word;
        }
    }

    &__actions {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__action-button {
        cursor: pointer;
    }

    &__inline-actions {
        display: flex;
        gap: 8px;
        justify-content: center;
    }

    .cell-text {
        word-break: keep-all;
    }

    &__pagination {
        display: grid;
        grid-template-columns: 2fr auto;
        margin-top: 20px;

        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }

        .total {
            display: flex;
            align-items: center;

            @media (max-width: 768px) {
                flex-direction: column;
            }
        }

        .prev_next {
            display: flex;

            @media (max-width: 768px) {
                justify-content: center;
                margin-top: 20px;
            }
        }
    }
}

::v-deep .base-table__label {
    word-break: keep-all !important;
}
</style>
